import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchPaymentSchedule = createAsyncThunk(
  'paymentSchedule/fetchSchedule',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await api.getPaymentSchedule(bookingId);
      return { bookingId, schedule: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePaymentSchedule = createAsyncThunk(
  'paymentSchedule/updateSchedule',
  async ({ bookingId, scheduleData }, { rejectWithValue }) => {
    try {
      const response = await api.updatePaymentSchedule(bookingId, scheduleData);
      return { bookingId, schedule: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const requestAdvancePayment = createAsyncThunk(
  'paymentSchedule/requestAdvance',
  async ({ bookingId, advanceData }, { rejectWithValue }) => {
    try {
      const response = await api.requestAdvancePayment(bookingId, advanceData);
      return { bookingId, advance: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const payMilestone = createAsyncThunk(
  'paymentSchedule/payMilestone',
  async ({ bookingId, milestoneId }, { rejectWithValue }) => {
    try {
      const response = await api.payMilestone(bookingId, milestoneId);
      return { bookingId, milestoneId, payment: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitReview = createAsyncThunk(
  'paymentSchedule/submitReview',
  async ({ bookingId, reviewData }, { rejectWithValue }) => {
    try {
      const response = await api.createBookingReview(bookingId, reviewData);
      return { bookingId, review: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  schedules: {}, // bookingId -> schedule data
  loading: false,
  error: null,
  lastUpdated: null,
};

const paymentScheduleSlice = createSlice({
  name: 'paymentSchedule',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateMilestoneStatus: (state, action) => {
      const { bookingId, milestoneId, status, paidAt } = action.payload;
      const schedule = state.schedules[bookingId];
      if (schedule && schedule.milestones) {
        const milestone = schedule.milestones.find(m => m.id === milestoneId);
        if (milestone) {
          milestone.status = status;
          milestone.paid_at = paidAt;
        }
      }
    },
    addAdvanceRequest: (state, action) => {
      const { bookingId, advanceRequest } = action.payload;
      const schedule = state.schedules[bookingId];
      if (schedule) {
        if (!schedule.advance_requests) {
          schedule.advance_requests = [];
        }
        schedule.advance_requests.push(advanceRequest);
      }
    },
    addReview: (state, action) => {
      const { bookingId, review } = action.payload;
      const schedule = state.schedules[bookingId];
      if (schedule) {
        if (!schedule.reviews) {
          schedule.reviews = [];
        }
        schedule.reviews.push(review);
      }
    },
    clearSchedule: (state, action) => {
      const bookingId = action.payload;
      delete state.schedules[bookingId];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Payment Schedule
      .addCase(fetchPaymentSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const { bookingId, schedule } = action.payload;
        state.schedules[bookingId] = schedule;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPaymentSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Payment Schedule
      .addCase(updatePaymentSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePaymentSchedule.fulfilled, (state, action) => {
        state.loading = false;
        const { bookingId, schedule } = action.payload;
        state.schedules[bookingId] = schedule;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updatePaymentSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Request Advance Payment
      .addCase(requestAdvancePayment.fulfilled, (state, action) => {
        const { bookingId, advance } = action.payload;
        const schedule = state.schedules[bookingId];
        if (schedule) {
          if (!schedule.advance_requests) {
            schedule.advance_requests = [];
          }
          schedule.advance_requests.push(advance);
        }
      })
      // Pay Milestone
      .addCase(payMilestone.fulfilled, (state, action) => {
        const { bookingId, milestoneId, payment } = action.payload;
        const schedule = state.schedules[bookingId];
        if (schedule && schedule.milestones) {
          const milestone = schedule.milestones.find(m => m.id === milestoneId);
          if (milestone) {
            milestone.paid_at = payment.paid_at;
            milestone.status = 'paid';
          }
        }
      })
      // Submit Review
      .addCase(submitReview.fulfilled, (state, action) => {
        const { bookingId, review } = action.payload;
        const schedule = state.schedules[bookingId];
        if (schedule) {
          if (!schedule.reviews) {
            schedule.reviews = [];
          }
          schedule.reviews.push(review);
        }
      });
  },
});

export const {
  clearError,
  updateMilestoneStatus,
  addAdvanceRequest,
  addReview,
  clearSchedule,
} = paymentScheduleSlice.actions;

export default paymentScheduleSlice.reducer;
